import { useState } from 'react'
import { faBank } from '@fortawesome/pro-regular-svg-icons'
import { Button, Input, Text, Toggle } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import { addUnconnectedAccount } from '@/actions/financialAccountActions'
import { WidgetCard } from './WidgetCard'
import { BUTTON_ROW, CardIcon } from './cardParts'
import {
  ACCOUNT_KINDS,
  EMPTY_ANSWERS,
  MAX_BANK_NAME_LENGTH,
  TRANSACTION_COUNT_OPTIONS,
  TRANSACTION_COUNTS,
  isComplete,
  toFewOutcome,
  toManyOutcome,
  type UnconnectedAccountAnswers,
} from './unconnectedAccount'
import type { WidgetProps } from './types'

export const UnconnectedAccountWidget = ({
  resolved,
  onOutcome,
}: WidgetProps) => {
  const dispatch = useAppDispatch()
  const [answers, setAnswers] =
    useState<UnconnectedAccountAnswers>(EMPTY_ANSWERS)
  const [saving, setSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)

  if (resolved) return null

  const answer = (next: Partial<UnconnectedAccountAnswers>) =>
    setAnswers((current) => ({ ...current, ...next }))

  const submit = async () => {
    if (!answers.kind) return
    if (answers.count === TRANSACTION_COUNTS.few) {
      const { text, display } = toFewOutcome(answers)
      onOutcome(text, display)
      return
    }
    setSaving(true)
    setSaveFailed(false)
    try {
      const result = await dispatch(
        addUnconnectedAccount({
          institutionName: answers.bank.trim(),
          mask: answers.lastFour,
          accountKind: answers.kind,
        })
      )
      if (!result) {
        setSaveFailed(true)
        return
      }
      const { text, display } = toManyOutcome(result)
      onOutcome(text, display)
    } catch {
      setSaveFailed(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <WidgetCard>
      <div className="flex items-start gap-3">
        <CardIcon icon={faBank} />
        <div className="flex flex-col">
          <Text variant={TypographyStyles.LABEL_MD}>Which bank is it?</Text>
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
            {"I'll still need its practice transactions"}
          </Text>
        </div>
      </div>
      <div className="flex flex-col gap-2 @md:flex-row">
        <Input
          aria-label="Bank name"
          placeholder="Bank name, e.g. Coastal Credit Union"
          inputClassName="body-sm"
          maxLength={MAX_BANK_NAME_LENGTH}
          disabled={saving}
          value={answers.bank}
          onChange={(e) => answer({ bank: e.target.value })}
        />
        <Input
          aria-label="Last four digits"
          placeholder="Last 4"
          inputClassName="body-sm"
          inputMode="numeric"
          maxLength={4}
          disabled={saving}
          value={answers.lastFour}
          onChange={(e) =>
            answer({ lastFour: e.target.value.replace(/\D/g, '') })
          }
          className="@md:w-28"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.BODY_SM}>
          What kind of account is it?
        </Text>
        <div role="group" aria-label="Kind of account" className={BUTTON_ROW}>
          {ACCOUNT_KINDS.map((kind) => (
            <Toggle
              key={kind.value}
              variant="outline"
              size="sm"
              className="body-sm"
              checked={answers.kind === kind.value}
              disabled={saving}
              onCheckedChange={() => answer({ kind: kind.value })}
            >
              {kind.label}
            </Toggle>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.BODY_SM}>
          About how many practice transactions since your catch-up period began?
        </Text>
        <div
          role="group"
          aria-label="How many practice transactions"
          className={BUTTON_ROW}
        >
          {TRANSACTION_COUNT_OPTIONS.map((option) => (
            <Toggle
              key={option.value}
              variant="outline"
              size="sm"
              className="body-sm"
              checked={answers.count === option.value}
              disabled={saving}
              onCheckedChange={() => answer({ count: option.value })}
            >
              {option.label}
            </Toggle>
          ))}
        </div>
      </div>
      {saveFailed && (
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.DANGER}>
          {"Couldn't save that. Try again."}
        </Text>
      )}
      <Button
        size="sm"
        variant="primary"
        className="w-fit"
        disabled={!isComplete(answers) || saving}
        loading={saving}
        onClick={() => void submit()}
      >
        Continue
      </Button>
    </WidgetCard>
  )
}
